import SidePanel from 'components/side-panel/SidePanel';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { PAYOUT_TXNS_PAGES, PAYOUT_TXNS_TABS, PAYOUT_TXNS_TYPE_FILTERS } from '../constants';
import PayoutTransactions from './PayoutTransactions';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { usePaginatedGetTransactionsByPayoutId } from '../hooks/usePaginatedGetTransactionsByPayoutIdQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import PayoutTransactionsFilters from './PayoutTransactionsFilters';
import { OutletContextType } from 'types/baseTypes';
import debounce from 'lodash/debounce';
import { usePaginatedGetUpcomingPayoutsTransactions } from '../hooks/usePaginatedGetUpcomingPayoutsTransactionsQuery';
import { selectCurrentTeam, selectCurrentTeamInvoicePermissions, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useLazyDownloadTransactionsByPayoutIdQuery, useLazyDownloadUpcomingPayoutsTransactionsQuery } from '../api';
import { NotifierTypes } from 'constants/notifierConstants';
import { logAnalyticEvent } from 'utils/analytics';
import { PayoutsCleverTapEvents } from '../events';
import { initialPayoutTxnsPageState, selectPayoutTxnsFilters, setPayoutTransactionFilters } from '../payoutsSlice';
import { getAmountFilterString, getPayoutTransactionTabsFilter } from '../utils';

const PayoutTransactionsPanel: FC = () => {
  const { payoutId, teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const selectedTxnFilters = useAppSelector(selectPayoutTxnsFilters);

  const isUpcomingPayoutsTransactions =
    location.pathname === `/payouts/balance/${teamId}/upcoming-payouts/balance/transactions`;

  const payoutPermissions = useAppSelector(selectCurrentTeamInvoicePermissions);

  const {
    groupedList: payoutTransactionsList,
    hasMore: hasMoreTxns,
    isFetching: isLoadingTxns,
    isError: isTxnsError,
    loadMore: loadPayoutTxns,
    isLoadingSuccess: isTxnsFetchSuccess,
    queryParams,
    resetQuery: resetTxnsQuery,
    setQueryParams,
    totalListCount,
    hasPermissionsToView
  } = isUpcomingPayoutsTransactions
    ? usePaginatedGetUpcomingPayoutsTransactions()
    : usePaginatedGetTransactionsByPayoutId({ payoutId: payoutId });

  const [downloadUpcomingPayoutsTxns] = useLazyDownloadUpcomingPayoutsTransactionsQuery();
  const [downloadPayoutsTxns] = useLazyDownloadTransactionsByPayoutIdQuery();

  const [page, setPage] = useState<PAYOUT_TXNS_PAGES>(PAYOUT_TXNS_PAGES.PAYOUT_TXNS);
  const [selectedTab, setSelectedTab] = useState<PAYOUT_TXNS_TABS>(PAYOUT_TXNS_TABS.PAYMENTS);
  const [appliedTypeFilters, setAppliedTypeFilters] = useState([]);
  const [appliedAmountFilter, setAppliedAmountFilter] = useState({ title: '', min: '', max: '' });
  const [searchText, setSearchText] = useState('');

  const initialMount = useRef(true);

  useEffect(() => {
    resetTxnsQuery();
  }, [payoutPermissions.viewReceiptsBySelf, payoutPermissions.viewReceiptsByTeam]);

  useEffect(() => {
    if (initialMount.current) {
      setSelectedTab(selectedTxnFilters.tab);
      setSearchText(selectedTxnFilters.search);
      setAppliedAmountFilter(selectedTxnFilters.amountFilter);
      if (selectedTxnFilters.tab === PAYOUT_TXNS_TABS.ALL) {
        setAppliedTypeFilters(selectedTxnFilters.typeFilter);
      }
      initialMount.current = false;
    }
  }, [selectedTxnFilters]);

  useEffect(() => {
    const amountFilter = getAmountFilterString(appliedAmountFilter);
    const typeFilter = getTypeFilterString(appliedTypeFilters);
    setQueryParams({ ...queryParams, search: searchText, status: typeFilter, amount: amountFilter });
  }, [appliedAmountFilter, appliedTypeFilters, searchText, selectedTab]);

  useEffect(() => {
    payoutTxnsLoader.current = loadPayoutTxns;
  }, [loadPayoutTxns]);

  const payoutTxnsLoader = useRef(loadPayoutTxns);

  const { setIntersectionElement } = useIntersectionObserver({
    intersectionCallBack: payoutTxnsLoader,
    threshold: 0.5
  });

  const { handleSidePanelClose } = useOutletContext<OutletContextType>();

  const handleSidePanelBack = () => {
    navigate(-1);
    dispatch(setPayoutTransactionFilters(initialPayoutTxnsPageState));
  };

  const onCloseSidePanel = () => {
    dispatch(setPayoutTransactionFilters(initialPayoutTxnsPageState));
    handleSidePanelClose();
  };

  const goToFiltersPanel = () => {
    setPage(PAYOUT_TXNS_PAGES.FILTERS);
  };

  const goToTransactionsPanel = () => {
    setPage(PAYOUT_TXNS_PAGES.PAYOUT_TXNS);
  };

  const getTypeFilterString = (type: PAYOUT_TXNS_TYPE_FILTERS[]) => {
    let typeFilterString = '';
    if (selectedTab === PAYOUT_TXNS_TABS.ALL) {
      typeFilterString = type.length > 0 && type.includes(PAYOUT_TXNS_TYPE_FILTERS.ALL) ? '' : type.join(',');
    } else if (selectedTab === PAYOUT_TXNS_TABS.PAYMENTS) {
      typeFilterString = getPayoutTransactionTabsFilter(selectedTab).join(',');
    }
    return typeFilterString;
  };

  const calculateFilterCount = () => {
    let totalFilters = 0;
    if (appliedTypeFilters.length > 0) {
      totalFilters = appliedTypeFilters.length;
    }
    if (getAmountFilterString(appliedAmountFilter).trim()) {
      totalFilters += 1;
    }
    return totalFilters;
  };

  const handleApplyFilters = filter => {
    const { type, amount } = filter ?? {};
    setAppliedAmountFilter(amount);
    setAppliedTypeFilters(type);
    if (selectedTab === PAYOUT_TXNS_TABS.ALL) {
      dispatch(setPayoutTransactionFilters({ ...selectedTxnFilters, amountFilter: amount, typeFilter: type }));
    } else {
      dispatch(
        setPayoutTransactionFilters({
          ...selectedTxnFilters,
          amountFilter: amount,
          typeFilter: [PAYOUT_TXNS_TYPE_FILTERS.COLLECTED, PAYOUT_TXNS_TYPE_FILTERS.REFUNDS]
        })
      );
    }
    setPage(PAYOUT_TXNS_PAGES.PAYOUT_TXNS);
    logAnalyticEvent(PayoutsCleverTapEvents.webPayoutsTransactionsFiltersApplied, {
      amountFilter: getAmountFilterString(amount),
      typeFilter: type.join(',')
    });
  };

  const onClearFilters = e => {
    e.stopPropagation();
    setAppliedAmountFilter({ title: '', min: '', max: '' });
    setAppliedTypeFilters([]);
    dispatch(
      setPayoutTransactionFilters({
        ...selectedTxnFilters,
        amountFilter: { title: '', min: '', max: '' },
        typeFilter: []
      })
    );
  };

  const handleTxnsTabChange = (tab: PAYOUT_TXNS_TABS) => {
    if (tab === PAYOUT_TXNS_TABS.ALL) {
      dispatch(setPayoutTransactionFilters({ ...selectedTxnFilters, typeFilter: [], tab }));
    } else {
      dispatch(
        setPayoutTransactionFilters({
          ...selectedTxnFilters,
          typeFilter: [PAYOUT_TXNS_TYPE_FILTERS.COLLECTED, PAYOUT_TXNS_TYPE_FILTERS.REFUNDS],
          tab
        })
      );
    }
    setSelectedTab(tab);
    setAppliedTypeFilters([]);
  };

  const handleExportClick = () => {
    isUpcomingPayoutsTransactions
      ? downloadUpcomingPayoutsTxns({ ...queryParams, limit: null, page: 0, teamId: currentTeam.id })
      : downloadPayoutsTxns({ ...queryParams, limit: null, page: 0, teamId: currentTeam.id, payoutId });
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading started in the background',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    logAnalyticEvent(
      isUpcomingPayoutsTransactions
        ? PayoutsCleverTapEvents.webUpcomingPayoutsTransactionsExport
        : PayoutsCleverTapEvents.webPayoutTransactionsExport,
      {
        amountFilter: appliedAmountFilter?.title ? getAmountFilterString(appliedAmountFilter) : 'All',
        typeFilter: appliedTypeFilters?.join(',') ?? '',
        searchText: searchText ?? ''
      }
    );
  };

  const handleSearchTermChange = (term: string) => {
    setSearchText(term);
    debouncedSearchHandler(term);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch('');
  };

  const initiateSearch = (searchTerm: string) => {
    logAnalyticEvent(PayoutsCleverTapEvents.webPayoutTransactionSearchApplied, { searchTerm: searchTerm });
    dispatch(
      setPayoutTransactionFilters({
        ...selectedTxnFilters,
        search: searchTerm
      })
    );
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [queryParams]);

  return (
    <SidePanel isOpen={true} onClose={onCloseSidePanel}>
      {page === PAYOUT_TXNS_PAGES.FILTERS && (
        <PayoutTransactionsFilters
          handleClose={goToTransactionsPanel}
          currentSelectedTxnTab={selectedTab}
          onApply={handleApplyFilters}
          appliedTypeFilters={appliedTypeFilters}
          appliedAmountFilter={appliedAmountFilter}
        />
      )}
      {page === PAYOUT_TXNS_PAGES.PAYOUT_TXNS && (
        <PayoutTransactions
          payoutId={payoutId}
          handleSidePanelClose={handleSidePanelBack}
          hasMoreTxns={hasMoreTxns}
          isLoadingTxns={isLoadingTxns}
          setIntersectionElement={setIntersectionElement}
          payoutTxns={payoutTransactionsList}
          isLoadingSuccess={isTxnsFetchSuccess}
          totalCount={totalListCount}
          onFilterClick={goToFiltersPanel}
          selectedTab={selectedTab}
          setSelectedTab={handleTxnsTabChange}
          isTxnsError={isTxnsError}
          searchText={searchText}
          handleSearchTermChange={handleSearchTermChange}
          onClearSearch={onClearSearch}
          hasPermissionsToView={hasPermissionsToView}
          onExportClick={handleExportClick}
          filterCount={calculateFilterCount()}
          clearFilters={onClearFilters}
        />
      )}
    </SidePanel>
  );
};

export default PayoutTransactionsPanel;
